/**
 * 401 Retry Interceptor Utility
 *
 * Adds a response interceptor that, on a 401 error, attempts a single token
 * refresh and retries the original request.  Only if the refresh also fails
 * does it clear credentials and redirect to /login.
 *
 * Usage (in any service that creates an axios instance):
 *
 *   import { applyAuthRetryInterceptor } from '../utils/applyAuthRetryInterceptor';
 *
 *   const myApiClient = axios.create({ ... });
 *   applyAuthRetryInterceptor(myApiClient);
 *
 * The interceptor is safe to combine with the proactive token-refresh request
 * interceptor — this one acts as a safety net for edge-case races.
 */

import config from '../config';

// ─── Constants ───────────────────────────────────────────────────────────────

const TOKEN_KEY = 'token';
const COGNITO_REFRESH_TOKEN_KEY = 'cognito_refresh_token';
const COGNITO_USERNAME_KEY = 'cognito_username';
const COGNITO_ACCESS_TOKEN_KEY = 'cognito_access_token';
const COGNITO_EXPIRES_AT_KEY = 'cognito_expires_at';

// ─── Shared refresh-in-progress promise ──────────────────────────────────────
// Prevents multiple concurrent refresh calls when several 401s arrive at once.
let _retryRefreshPromise = null;

/**
 * Attempt a token refresh via the auth service.
 *
 * Uses a shared in-flight promise so concurrent 401 responses don't each
 * trigger their own refresh request.
 *
 * @returns {Promise<boolean>} true if refresh succeeded
 */
const attemptTokenRefresh = async () => {
  if (_retryRefreshPromise) return _retryRefreshPromise;

  _retryRefreshPromise = (async () => {
    const refreshToken = localStorage.getItem(COGNITO_REFRESH_TOKEN_KEY);
    const username = localStorage.getItem(COGNITO_USERNAME_KEY);
    if (!refreshToken) return false;

    try {
      const response = await fetch(
        `${config.api.authServiceUrl}/auth/refresh`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            refresh_token: refreshToken,
            username,
          }),
        }
      );

      if (!response.ok) return false;

      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem(TOKEN_KEY, data.access_token);
        localStorage.setItem(COGNITO_ACCESS_TOKEN_KEY, data.access_token);

        if (data.expires_in) {
          const expiresAt = Date.now() + data.expires_in * 1000;
          localStorage.setItem(COGNITO_EXPIRES_AT_KEY, expiresAt.toString());
        }
        if (data.refresh_token) {
          localStorage.setItem(COGNITO_REFRESH_TOKEN_KEY, data.refresh_token);
        }
        return true;
      }
      return false;
    } catch {
      return false;
    }
  })().finally(() => {
    _retryRefreshPromise = null;
  });

  return _retryRefreshPromise;
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Attach a 401 retry-after-refresh response interceptor to the given axios
 * instance.  On a 401, it will:
 *   1. Try to refresh the access token (once per request).
 *   2. If the refresh succeeds, retry the original request with the new token.
 *   3. If the refresh fails, clear credentials and redirect to /login.
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 */
export const applyAuthRetryInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const refreshed = await attemptTokenRefresh();
        if (refreshed) {
          originalRequest.headers['Authorization'] =
            `Bearer ${localStorage.getItem(TOKEN_KEY)}`;
          return axiosInstance(originalRequest);
        }

        // Refresh failed — force logout
        localStorage.removeItem(TOKEN_KEY);
        window.location.href = '/login';
      }

      return Promise.reject(error);
    }
  );
};

export default applyAuthRetryInterceptor;
